import { Card, CardContent } from "../ui/card";
import AllDesigns from "./AllDesigns";
import { useUserId } from "./UserIdContext";
import UserDesign from "./UserDesign";
import { Ban, ChevronLeft, RefreshCw, Trash2 } from "lucide-react";
import AssignRole from "./AssignRole";
import ApplicatorReview from "./ApplicatorReview";
import SetResinCost from "./SetResinCost";
import { useState } from "react";
import {
  Toast,
  ToastDescription,
  ToastTitle,
  ToastViewport,
} from "../ui/toast";
import CreateCoupon from "./CreateCoupon";
import AllCoupons from "./AllCoupons";
// import AllRequests from "./AllRequests";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

export default function DashboardContent({
  activeSection,
  setActiveSection,
  customers,
  applicators,
  designs,
  setDesigns,
  setCustomers,
  setApplicators,
  handleFetchApplicators,
  handleFetchCustomers,
  coupons,
  setCoupons,
}) {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const { setUserId } = useUserId();

  const [toastList, setToastList] = useState([]);

  const handleRemoveUser = async (id, userType) => {
    try {
      const token = localStorage.getItem("x-auth-token");
      //   console.log(token);
      const response = await fetch(`${API_URL}/api/user/remove/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "x-auth-token": token,
        },
      });

      const responseData = await response.json();

      if (response.ok) {
        // console.log(`User ${id} removed successfully`)

        // Update UI by removing the user from state
        if (userType === "customer") {
          setCustomers((prevCustomers) =>
            prevCustomers.filter((c) => c._id !== id),
          );
        } else if (userType === "applicator") {
          setApplicators((prevApplicators) =>
            prevApplicators.filter((a) => a._id !== id),
          );
        }

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: responseData.data.message || "!",
            variant: "default",
          },
        ]);
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description: responseData.data.message || "!",
            variant: "destructive",
          },
        ]);
        console.error("Failed to delete user:", responseData.message);
      }
    } catch (error) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: error || "Something went wrong!",
          variant: "destructive",
        },
      ]);
      console.error("Error deleting user:", error);
    }
  };

  const handleSuspendUser = async (id, userType, shouldSuspend) => {
    try {
      const token = localStorage.getItem("x-auth-token");
      //   console.log(token);

      const response = await fetch(
        `${API_URL}/api/user/suspend-or-revive/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
            "x-auth-token": token,
          },
          body: JSON.stringify({ suspendUser: shouldSuspend }),
        },
      );

      const responseData = await response.json();

      if (response.ok) {
        // Update UI to reflect suspension status
        if (userType === "customer") {
          setCustomers((prevCustomers) =>
            prevCustomers.map((c) =>
              c._id === id ? { ...c, suspendUser: shouldSuspend } : c,
            ),
          );
        } else if (userType === "applicator") {
          setApplicators((prevApplicators) =>
            prevApplicators.map((a) =>
              a._id === id ? { ...a, suspendUser: shouldSuspend } : a,
            ),
          );
        }

        // ✅ Fetch fresh data from the backend
        if (userType === "customer") {
          handleFetchCustomers();
        } else if (userType === "applicator") {
          handleFetchApplicators();
        }

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: shouldSuspend
              ? responseData.data.message
              : "User restored successfully",
            variant: "default",
          },
        ]);
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data.message || "Failed to update user status!",
            variant: "destructive",
          },
        ]);
        console.error("Failed to delete user:", responseData.message);
      }
    } catch (error) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: error || "Something went wrong!",
          variant: "destructive",
        },
      ]);
      console.error("Error deleting user:", error);
    }
  };

  const renderTable = (data, type) => (
    <div className="tw-overflow-x-auto">
      <table className="tw-w-full tw-border-collapse tw-rounded-lg tw-bg-white">
        <thead className="tw-text-center">
          <tr className="tw-bg-[#00B5ED] tw-text-white">
            <th className="tw-px-4 tw-py-2">Name</th>
            <th className="tw-px-4 tw-py-2">ID</th>
            {type === "applicator" ? (
              <th className="tw-px-4 tw-py-2">Company</th>
            ) : (
              <th className="tw-px-4 tw-py-2"></th> // Ensures proper column alignment
            )}
            <th className="tw-px-4 tw-py-2">Role</th>
            <th className="tw-px-4 tw-py-2">Email</th>
            <th className="tw-px-4 tw-py-2">Actions</th>
          </tr>
        </thead>
        <tbody className="tw-text-center">
          {data.length > 0 ? (
            data.map((user) => (
              <tr key={user._id} className="tw-border-b hover:tw-bg-gray-100">
                <td className="tw-px-4 tw-py-2">
                  {user.firstName} {user.lastName}
                </td>
                <td className="tw-px-4 tw-py-2">{user.applicatorId}</td>
                {type === "applicator" ? (
                  <td className="tw-px-4 tw-py-2">{user.companyName}</td>
                ) : (
                  <td className="tw-px-10 tw-py-2"></td> // Keeps columns aligned for customers
                )}
                <td className="tw-px-4 tw-py-2 tw-font-medium">{user.role}</td>
                <td className="tw-px-4 tw-py-2">{user.email}</td>
                <td className="tw-px-4 tw-py-2 tw-text-center tw-align-middle">
                  <div className="tw-inline-flex tw-items-center tw-justify-center">
                    <button
                      className="tw-bg-[#00B5ED] tw-text-white tw-py-1 tw-px-3 tw-rounded-md tw-mr-2"
                      onClick={() => {
                        setUserId(user._id);
                        setActiveSection("User Design");
                      }}
                    >
                      View Designs
                    </button>

                    <button
                      className={`tw-mr-2  tw-text-white tw-py-1 tw-px-2 tw-rounded-md ${
                        user.isSuspended ? "tw-bg-green-500" : "tw-bg-amber-400"
                      }`}
                      onClick={() =>
                        handleSuspendUser(user._id, type, !user.isSuspended)
                      }
                    >
                      {user.isSuspended ? (
                        <RefreshCw className="tw-w-4 tw-h-4 tw-my-1" />
                      ) : (
                        <Ban className="tw-w-4 tw-h-4 tw-my-1" />
                      )}
                    </button>

                    <button
                      className="tw-bg-red-500 tw-text-white tw-py-1 tw-px-2 tw-rounded-md"
                      onClick={() => handleRemoveUser(user._id, type)}
                    >
                      <Trash2 className="tw-w-4 tw-h-4 tw-my-1" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={type === "applicator" ? 5 : 4}
                className="tw-px-4 tw-py-2 tw-text-center"
              >
                No {type === "applicator" ? "applicators" : "customers"} found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <main className="tw-flex-1 tw-p-6 tw-bg-[#f5f5f5] tw-border-l-[3px] tw-border-[#00B5ED] tw-rounded-e-md">
      {/* Back Button - Only Show If Not Home */}
      {activeSection !== "Home" && (
        <button
          onClick={() => setActiveSection("Home")}
          className="tw-flex tw-items-center tw-text-sm tw-text-[#00B5ED] focus:tw-bg-transparent hover:tw-bg-transparent tw-font-semibold tw-mb-4 tw-transition hover:tw-opacity-80"
        >
          <ChevronLeft className="tw-w-5 tw-h-5 tw-mr-2 " /> Back to Home
        </button>
      )}

      <h1 className="tw-text-xl tw-ml-2 tw-font-bold">{activeSection}</h1>

      {/* User Management */}
      {activeSection === "Applicators" && (
        <Card className="tw-mt-4 tw-p-6">
          <CardContent>
            <p className="tw-font-semibold tw-mb-2">List of Applicators:</p>
            {renderTable(applicators, "applicator")}
          </CardContent>
        </Card>
      )}

      {activeSection === "Customers" && (
        <Card className="tw-mt-4 tw-p-6">
          <CardContent>
            <p className="tw-font-semibold tw-mb-2">List of Customers:</p>
            {renderTable(customers, "customer")}
          </CardContent>
        </Card>
      )}

      {/* Design Overview */}
      {activeSection === "All Designs" && (
        <Card className="tw-mt-4 tw-p-6 tw-max-h-[calc(100vh-140px)] tw-overflow-auto tw-scrollbar-none">
          <CardContent>
            <AllDesigns designs={designs} setDesigns={setDesigns} />
          </CardContent>
        </Card>
      )}

      {/* Design Overview
      {activeSection === "All Requests" && (
        <Card className="tw-mt-4 tw-p-6 tw-max-h-[calc(100vh-140px)] tw-overflow-auto tw-scrollbar-none">
          <CardContent>
            <AllRequests designs={designs} setDesigns={setDesigns} />
          </CardContent>
        </Card>
      )} */}

      {/* Pricing */}
      {activeSection === "Resin Cost" && <SetResinCost />}

      {activeSection === "Create Coupon" && <CreateCoupon />}

      {activeSection === "All Coupons" && (
        <Card className="tw-mt-4 tw-p-6 tw-max-h-[calc(100vh-140px)] tw-overflow-auto tw-scrollbar-none">
          <CardContent>
            <AllCoupons coupons={coupons} setCoupons={setCoupons} />
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      {activeSection === "Assign Role" && <AssignRole />}

      {activeSection === "Applicator Review" && <ApplicatorReview />}

      {activeSection === "User Design" && (
        <Card className="tw-mt-4 tw-p-6 tw-max-h-[calc(100vh-140px)] tw-overflow-auto tw-scrollbar-none">
          <CardContent>
            <UserDesign />
          </CardContent>
        </Card>
      )}

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </main>
  );
}
